/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  Checks for `?beta=detail,whatever` in the URL
 */

import type { LocationDescriptor, Location as Location__legacy } from 'history'
import { useMemo } from 'react'
import { type LinkProps, useLocation } from 'react-router-dom'
import { type Path, type To, parsePath } from 'react-router-dom-v5-compat'

export enum BetaFeature {
  /**
   * Disables BE result sort/limit, returns up to 10 location + 10 building results
   */
  AUTOCOMPLETE_ALL_RESULTS = 'all',
  DLC_ORDER = 'dlc_order',
  DRAFT = 'draft',
  GALLERY_CALL = 'gallery-call',
  GALLERY_OFFERS = 'gallery-offers',
  /**
   * Enables detail gallery landing experiment from a map list entry point
   */
  GALLERY_DIRECT = 'gallery_direct',
  GALLERY_INDIRECT = 'gallery_indirect',
  MM_MODAL_CTA = 'mm_cta',
  MOCK_TOUR_AVAILABILITY = 'mocktour',
  NO_CLIENT_SIDE_VALIDATION = 'novalidate',
  SIMILAR_LISTINGS = 'similar',
  SIMILAR_LISTINGS_GALLERY = 'similar-gallery',
  XFR_NAV = 'xfr_nav',
  ZOE = 'zoe',
  ZOE_MAPLIST = 'zoe-maplist',
  APPS_FLYER = 'apps-flyer',
  NO_ADS = 'no-ads',
}

export const isBetaFeatureEnabled = (
  { search }: Pick<Partial<Path>, 'search'>,
  feature: BetaFeature
) => {
  return !!(search && new URLSearchParams(search).get('beta')?.split(',').includes(feature))
}

export const isAnyBetaFeatureEnabled = ({ search }: Pick<Partial<Path>, 'search'>) => {
  return !!(search && new URLSearchParams(search).has('beta'))
}

export const useIsBetaFeatureEnabled = (feature: BetaFeature) => {
  const { search } = useLocation()
  return useMemo(() => isBetaFeatureEnabled({ search }, feature), [feature, search])
}

/**
 * @deprecated use keepBetaParamTo instead
 *
 * If the current page has a `?beta` param in the query string, adjust the given `to` value to also include that
 * `?beta` param
 *
 * @param initialTo
 * @example
 * <Link to={keepBetaParam('/somewhere')}>Allons-y</Link>
 */
export function keepBetaParam(initialTo: undefined): undefined
export function keepBetaParam(
  initialTo: string
): (currentLocation: Location__legacy<unknown>) => LocationDescriptor<undefined>
export function keepBetaParam(
  initialTo: string | undefined
): ((currentLocation: Location__legacy<unknown>) => LocationDescriptor<undefined>) | undefined
export function keepBetaParam<ToLocationState>(
  initialTo: LinkProps<ToLocationState>['to']
): (currentLocation: Location__legacy<unknown>) => LocationDescriptor<ToLocationState>
export function keepBetaParam<ToLocationState>(
  initialTo: LinkProps<ToLocationState>['to'] | undefined
): ((currentLocation: Location__legacy<unknown>) => LocationDescriptor<ToLocationState>) | undefined
export function keepBetaParam<ToLocationState>(
  initialTo: LinkProps<ToLocationState>['to'] | undefined
):
  | ((currentLocation: Location__legacy<unknown>) => LocationDescriptor<ToLocationState>)
  | undefined {
  return initialTo === undefined
    ? undefined
    : (location) => {
        // @ts-expect-error react-router-dom's types assume CurrentLocationState and ToLocationState are the same type
        const to = typeof initialTo === 'function' ? initialTo(location) : initialTo
        const toObject = typeof to === 'string' ? new URL(to, 'http://example.invalid/') : to
        if (
          isAnyBetaFeatureEnabled(location) &&
          (!toObject.search || !isAnyBetaFeatureEnabled({ search: toObject.search }))
        ) {
          return {
            hash: toObject.hash,
            // @ts-expect-error key doesn't exist on URL
            key: toObject.key,
            pathname: toObject.pathname,
            search: `${toObject.search ?? ''}${
              toObject.search ? '&' : '?'
            }beta=${encodeURIComponent(new URLSearchParams(location.search).get('beta') ?? '')}`,
            // @ts-expect-error state doesn't exist on URL
            state: toObject.state,
          }
        }
        return to
      }
}

/**
 * If the current page has a `?beta` param in the query string, adjust the given `to` value to also include that
 * `?beta` param, if it doesn't already have a beta param of its own
 */
export function keepBetaParamTo(to: To, location: Partial<Path>): Partial<Path> {
  const pathObject = typeof to === 'string' ? parsePath(to) : to
  if (
    isAnyBetaFeatureEnabled(location) &&
    (!pathObject.search || !isAnyBetaFeatureEnabled(pathObject))
  ) {
    return {
      hash: pathObject.hash,
      pathname: pathObject.pathname,
      search: `${pathObject.search ?? ''}${
        pathObject.search ? '&' : '?'
      }beta=${encodeURIComponent(new URLSearchParams(location.search).get('beta') ?? '')}`,
    }
  }
  return pathObject
}
